import React, { useState } from 'react';
import { UserPlus, PencilSimple, Power, Copy, CheckCircle } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND } from '_helpers';
import { PageHeader, Bezel, Table, Button, IconButton, Modal, Input, Select, Tag, StatCard, MotionReveal } from '_ui';

const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || ''));

function classesSummary(enrollments = []) {
    if (!enrollments.length) return <span className="text-espresso-soft/50">—</span>;
    return (
        <div className="space-y-0.5">
            {enrollments.map(en => (
                <div key={en._id} className="text-espresso-soft">
                    {en.className} <span className="tnum text-espresso-soft/70">· {formatVND(en.tuitionPrice)}</span>
                </div>
            ))}
        </div>
    );
}

export function HocVien() {
    const { data, loading, refetch } = useFetch('/api/students');
    const api = useApi();
    const alert = useAlertActions();
    const [editing, setEditing] = useState(null);     // student | {} (new) | null
    const [credentials, setCredentials] = useState(null); // { email, password }
    const [showInactive, setShowInactive] = useState(false);

    const students = data || [];
    const activeCount = students.filter(s => s.active).length;
    const rows = students.filter(s => showInactive || s.active);

    async function toggleActive(st) {
        const res = await api.patch(`/api/students/${st._id}`, { active: !st.active });
        if (res.ok) { alert.success(st.active ? 'Đã tắt tài khoản' : 'Đã bật tài khoản'); refetch(); }
        else alert.error('Không thể cập nhật', res.error);
    }

    const columns = [
        { key: 'name', header: 'Tên', render: s => <span className={s.active ? 'font-medium text-espresso' : 'text-espresso-soft/60'}>{s.name}</span> },
        { key: 'email', header: 'Email', render: s => <span className="text-espresso-soft">{s.email}</span> },
        { key: 'classes', header: 'Lớp học', render: s => classesSummary(s.enrollments) },
        { key: 'status', header: 'Trạng thái', align: 'center', render: s => <Tag tone={s.active ? 'sage' : 'neutral'}>{s.active ? 'Đang hoạt động' : 'Đã tắt'}</Tag> },
        { key: 'actions', header: '', align: 'right', render: s => (
            <div className="flex justify-end gap-1">
                <IconButton icon={PencilSimple} label="Sửa" variant="ghost" onClick={() => setEditing(s)} />
                <IconButton icon={Power} label={s.active ? 'Tắt' : 'Bật'} variant="ghost"
                    className={s.active ? 'hover:text-danger' : 'hover:text-sage-deep'} onClick={() => toggleActive(s)} />
            </div>
        ) },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Lớp nail"
                title="Học viên"
                description="Thêm học viên, xếp lớp, đặt học phí và bật/tắt tài khoản đăng nhập."
                actions={
                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setShowInactive(v => !v)}>
                            {showInactive ? 'Ẩn tài khoản đã tắt' : 'Hiện tài khoản đã tắt'}
                        </Button>
                        <Button icon={UserPlus} onClick={() => setEditing({})}>Thêm học viên</Button>
                    </div>
                }
            />

            <div className="grid gap-5 sm:grid-cols-2">
                <MotionReveal><StatCard label="Đang hoạt động" value={activeCount} /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Tổng học viên" value={students.length} /></MotionReveal>
            </div>

            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={rows} loading={loading} empty={showInactive ? 'Chưa có học viên nào.' : 'Không có tài khoản đang hoạt động.'} />
            </Bezel>

            {editing && (
                <StudentModal
                    student={editing}
                    onClose={() => setEditing(null)}
                    onSaved={(creds) => { setEditing(null); refetch(); if (creds) setCredentials(creds); }}
                />
            )}
            {credentials && <CredentialsModal creds={credentials} onClose={() => setCredentials(null)} alert={alert} />}
        </div>
    );
}

function StudentModal({ student, onClose, onSaved }) {
    const isEdit = !!student._id;
    const api = useApi();
    const alert = useAlertActions();
    const { data: classes } = useFetch('/api/classes');
    const [form, setForm] = useState({
        name: student.name || '', email: student.email || '', password: '',
        classId: '', tuitionPrice: '',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const classOptions = (classes || []).filter(c => c.active).map(c => ({ value: c._id, label: `${c.name} · ${c.totalSessions} buổi` }));

    function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

    function validate() {
        const e = {};
        if (!form.name.trim()) e.name = 'Nhập tên';
        if (!isEmail(form.email)) e.email = 'Email không hợp lệ';
        if (!isEdit && form.password && form.password.length < 6) e.password = 'Ít nhất 6 ký tự';
        if (!isEdit && !form.classId) e.classId = 'Chọn lớp học';
        if (!isEdit && (form.tuitionPrice === '' || Number(form.tuitionPrice) < 0)) e.tuitionPrice = 'Học phí không hợp lệ';
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function save() {
        if (!validate()) return;
        setSaving(true);
        let res;
        if (isEdit) {
            res = await api.patch(`/api/students/${student._id}`, { name: form.name.trim(), email: form.email.trim() });
        } else {
            const body = {
                name: form.name.trim(), email: form.email.trim(),
                classId: form.classId, tuitionPrice: Number(form.tuitionPrice),
            };
            if (form.password) body.password = form.password;
            res = await api.post('/api/students', body);
        }
        setSaving(false);
        if (res.ok) {
            alert.success(isEdit ? 'Đã lưu học viên' : 'Đã thêm học viên');
            const generated = !isEdit && res.data?.generatedPassword;
            onSaved(generated ? { email: form.email.trim(), password: res.data.generatedPassword } : null);
        } else {
            alert.error('Không thể lưu', res.error);
        }
    }

    return (
        <Modal open onClose={onClose} eyebrow="Học viên" title={isEdit ? 'Sửa học viên' : 'Thêm học viên'}
            footer={<><Button variant="ghost" onClick={onClose}>Huỷ</Button><Button onClick={save} loading={saving}>Lưu</Button></>}>
            <div className="space-y-5">
                <Input label="Họ tên" value={form.name} onChange={e => set('name', e.target.value)} error={errors.name} placeholder="VD: Nguyễn Thị B" />
                <Input label="Email đăng nhập" type="email" value={form.email} onChange={e => set('email', e.target.value)} error={errors.email} placeholder="b@example.com" />
                {!isEdit && (
                    <>
                        <Input label="Mật khẩu (để trống sẽ tự tạo)" value={form.password} onChange={e => set('password', e.target.value)} error={errors.password} hint="Nếu để trống, hệ thống tạo mật khẩu ngẫu nhiên để bạn đưa cho học viên." />
                        <Select label="Lớp học" value={form.classId} onChange={v => set('classId', v)} options={classOptions} placeholder="Chọn lớp" error={errors.classId} />
                        <Input label="Học phí thoả thuận (VND)" type="number" min="0" value={form.tuitionPrice} onChange={e => set('tuitionPrice', e.target.value)} error={errors.tuitionPrice} placeholder="10000000" />
                    </>
                )}
            </div>
        </Modal>
    );
}

function CredentialsModal({ creds, onClose, alert }) {
    function copy() {
        if (navigator.clipboard) navigator.clipboard.writeText(`Email: ${creds.email}\nMật khẩu: ${creds.password}`).then(() => alert.success('Đã sao chép'));
    }
    return (
        <Modal open onClose={onClose} eyebrow="Tài khoản mới" title="Mật khẩu đăng nhập"
            footer={<Button icon={CheckCircle} onClick={onClose}>Đã lưu lại</Button>}>
            <div className="flex items-start gap-3 rounded-2xl bg-brass/10 p-4 ring-1 ring-brass/20">
                <p className="text-sm text-espresso-soft">
                    Đưa thông tin này cho học viên. <span className="font-semibold text-espresso">Mật khẩu chỉ hiện một lần.</span>
                </p>
            </div>
            <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between rounded-2xl bg-cream-200/60 px-4 py-3 ring-1 ring-espresso/10">
                    <span className="text-sm text-espresso-soft">Email</span>
                    <span className="font-medium text-espresso">{creds.email}</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-cream-200/60 px-4 py-3 ring-1 ring-espresso/10">
                    <span className="text-sm text-espresso-soft">Mật khẩu</span>
                    <span className="tnum font-semibold tracking-wide text-espresso">{creds.password}</span>
                </div>
                <button onClick={copy} className="inline-flex items-center gap-2 text-sm font-medium text-sage-deep transition-colors hover:text-espresso">
                    <Copy size={16} weight="light" /> Sao chép email &amp; mật khẩu
                </button>
            </div>
        </Modal>
    );
}
